// Firestore Image Helpers - Handles image data structure for Firestore
// Ensures consistent image format across the application

/**
 * Normalize image data for Firestore storage
 * Removes base64 data and keeps only Storage URLs
 * @param {Object|string} image - Image data (object with imageUrl/base64Data or string URL)
 * @returns {Object|string} - Normalized image (object with imageUrl or string URL)
 */
export const normalizeImageForFirestore = (image) => {
  if (!image) return null;

  // If it's already a string URL (Storage URL)
  if (typeof image === 'string') {
    // Only keep valid Storage URLs, remove base64
    if (image.startsWith('https://') && !image.startsWith('blob:')) {
      return image;
    }
    // Remove base64 strings
    if (image.startsWith('data:image')) {
      return null; // Don't store base64
    }
    return null;
  }

  // If it's an object
  if (typeof image === 'object') {
    // Prefer imageUrl (Storage URL) over base64Data
    if (image.imageUrl && 
        typeof image.imageUrl === 'string' && 
        image.imageUrl.startsWith('https://') && 
        !image.imageUrl.startsWith('blob:')) {
      // Return clean object with only Storage URL
      return {
        imageUrl: image.imageUrl,
        path: image.path || null,
        size: image.size || null
      };
    }
    
    // If no valid Storage URL, return null (don't store base64)
    return null;
  }

  return null;
};

/**
 * Normalize product images array for Firestore
 * Removes all base64 data, keeps only Storage URLs
 * @param {Array} images - Array of image objects or strings
 * @returns {Array} - Normalized array with only Storage URLs
 */
export const normalizeProductImagesForFirestore = (images) => {
  if (!Array.isArray(images)) {
    return [];
  }

  return images
    .map(img => normalizeImageForFirestore(img))
    .filter(Boolean); // Remove null values
};

/**
 * Prepare product data for Firestore (removes base64)
 * @param {Object} productData - Product data object
 * @returns {Object} - Clean product data without base64
 */
export const prepareProductDataForFirestore = (productData) => {
  const cleanData = { ...productData };

  // Remove base64Data field
  delete cleanData.base64Data;

  // Normalize primary image
  if (cleanData.imageUrl) {
    const normalized = normalizeImageForFirestore(cleanData.imageUrl);
    cleanData.imageUrl = normalized || null;
  }

  // Normalize images array
  if (cleanData.images && Array.isArray(cleanData.images)) {
    cleanData.images = normalizeProductImagesForFirestore(cleanData.images);
  }

  // Remove any other base64-related fields
  delete cleanData.imageFileName; // Legacy field
  delete cleanData.img; // Legacy field if it contains base64

  return cleanData;
};

/**
 * Extract image URLs from product (for display)
 * @param {Object} product - Product object
 * @returns {Array<string>} - Array of image URLs
 */
export const extractImageUrlsFromProduct = (product) => {
  if (!product) return [];

  const urls = [];

  // Check images array first
  if (product.images && Array.isArray(product.images)) {
    product.images.forEach(img => {
      if (typeof img === 'string' && img.startsWith('https://')) {
        urls.push(img);
      } else if (img && typeof img === 'object' && img.imageUrl) {
        if (img.imageUrl.startsWith('https://')) {
          urls.push(img.imageUrl);
        }
      }
    });
  }

  // Check primary imageUrl
  if (product.imageUrl && 
      typeof product.imageUrl === 'string' && 
      product.imageUrl.startsWith('https://')) {
    if (!urls.includes(product.imageUrl)) {
      urls.unshift(product.imageUrl); // Add to beginning
    }
  }

  return urls;
};

/**
 * Get primary image URL from product
 * @param {Object} product - Product object
 * @returns {string|null} - Primary image URL or null
 */
export const getPrimaryImageUrl = (product) => {
  if (!product) return null;

  // Check primary imageUrl first
  if (product.imageUrl && 
      typeof product.imageUrl === 'string' && 
      product.imageUrl.startsWith('https://')) {
    return product.imageUrl;
  }

  // Check images array
  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    const firstImage = product.images[0];
    if (typeof firstImage === 'string' && firstImage.startsWith('https://')) {
      return firstImage;
    }
    if (firstImage && typeof firstImage === 'object' && firstImage.imageUrl) {
      if (firstImage.imageUrl.startsWith('https://')) {
        return firstImage.imageUrl;
      }
    }
  }

  return null;
};











